import {
	flip,
	FloatingFocusManager,
	FloatingPortal,
	offset,
	shift,
	useClick,
	useFloating,
	useInteractions,
	useRole,
} from "@floating-ui/react";
import { XCircleIcon } from "@heroicons/react/20/solid";
import { DatesProvider, MonthPicker } from "@mantine/dates";
import clsx from "clsx";
import { endOfMonth, startOfMonth } from "date-fns";
import { type FunctionComponent, memo, useMemo, useState } from "react";

import type { ButtonVariantsProps } from "~/components/buttons/Button/buttonVariants.ts";
import type { FormInputBaseVariants} from "~/components/formElements/formInputBaseVariant.ts";
import { formInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import useOnClickOutside from "~/hooks/useOnClickOutside.ts";
import type { DateRangeNullable } from "~/types/dateAndTimeTypes.ts";
import { formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";

type Props = ButtonVariantsProps & FormInputBaseVariants & {
	selectedPeriod: DateRangeNullable;
	onChange: (period: DateRangeNullable) => void;
};

const DeliveryPeriodFilter: FunctionComponent<Props> = memo(({ selectedPeriod, onChange, inputSize }) => {
	const [isOpen, setIsOpen] = useState(false);
	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		middleware: [offset(4), flip(), shift()],
		placement: "top-start",
	});

	const startDate = selectedPeriod.startDate;
	const endDate = selectedPeriod.endDate;

	const [tempDateRange, setTempDateRange] = useState<[Date | null, Date | null]>([
		startDate || null,
		endDate || null,
	]);

	// Handle range selection
	const handleRangeChange = (value: [Date | null, Date | null]) => {
		const [tempStart, tempEnd] = value;
		setTempDateRange(value);
		if (tempStart) {
			let endOfMonthEndDate = tempEnd;
			if (!tempEnd) {
				endOfMonthEndDate = endOfMonth(tempStart);
			} else {
				endOfMonthEndDate = endOfMonth(tempEnd);
			}
			const newStartDate = startOfMonth(tempStart);
			const newEndDate = endOfMonthEndDate;
			onChange({ startDate: newStartDate, endDate: newEndDate });
		} else {
			onChange({ startDate: null, endDate: null });
		}
	};

	const applyFilter = () => {
		const [tempStart, tempEnd] = tempDateRange;
		if (tempStart) {
			let endOfMonthEndDate = tempEnd;
			if (!tempEnd) {
				endOfMonthEndDate = endOfMonth(tempStart);
			} else {
				endOfMonthEndDate = endOfMonth(tempEnd);
			}
			const newStartDate = startOfMonth(tempStart);
			const newEndDate = endOfMonthEndDate;
			onChange({ startDate: newStartDate, endDate: newEndDate });
		} else {
			onChange({ startDate: null, endDate: null });
		}
		setIsOpen(false);
	};

	// Handle popover close
	const handlePopoverClose = () => {
		applyFilter();
	};

	const buttonText = useMemo(() => {
		if (!startDate || !endDate) {
			return "Leistungszeitraum wählen";
		}

		return formatSmartDateRange(startDate, endDate, {
			month: "MMMM",
			year: " yyyy",
		});
	}, [endDate, startDate]);

	const click = useClick(context);
	const role = useRole(context);
	const { getReferenceProps, getFloatingProps } = useInteractions([click, role]);

	useOnClickOutside(refs.floating, handlePopoverClose);

	return (
		<>
			<div className="relative">
				<button
					className={clsx(formInputBaseVariants({inputSize}))}
					ref={refs.setReference}
					{...getReferenceProps({ onClick: (e) => e.stopPropagation() })}
				>
					<span className={ (!startDate && !endDate) ? "text-gray-400":""}>{buttonText}</span>
				</button>
				{startDate && endDate&& !isOpen && <div className="absolute right-0 top-0 h-full">
					<button onClick={() => onChange({ startDate: null, endDate: null })}
							className="flex h-full items-center pr-2">
						<XCircleIcon className="size-5 fill-gray-300" />
					</button>
				</div>}
			</div>

			{isOpen && (
				<FloatingPortal>
					<FloatingFocusManager context={context}
										  modal={false}>
						<div
							ref={refs.setFloating}
							style={floatingStyles}
							className="z-50 rounded-md bg-white px-3 py-4 shadow-lg ring-1 ring-black/5"
							{...getFloatingProps()}
						>
							<div className="w-72">
								<DatesProvider settings={{ locale: "de" }}>
									<MonthPicker
										type="range"
										value={tempDateRange}
										onChange={handleRangeChange}
										classNames={{
											monthsListControl: `px-2 py-1 text-center hover:bg-primary-400 hover:text-white rounded-md text-sm py-2 w-full
											  data-[disabled="true"]:hover:bg-white data-[disabled="true"]:text-gray-300
											  data-[selected]:bg-accent-500 data-[in-range]:bg-primary-200`,
											monthsList: "w-full",
											calendarHeader: "flex justify-between h-10 bg-white",
											calendarHeaderLevel: "flex gap-1 justify-center items-center hover:bg-gray-100 rounded-full px-4",
											calendarHeaderControl: "flex items-center justify-center w-10 h-10 hover:bg-gray-100 rounded-full data-[disabled]:opacity-0",
											calendarHeaderControlIcon: "text-gray-400 data-[direction='previous']:rotate-90 data-[direction='next']:-rotate-90",
											yearsListControl: `px-2 py-1 text-center hover:bg-primary-400 hover:text-white rounded-md text-sm py-2 w-full
											  data-[disabled="true"]:hover:bg-white data-[disabled="true"]:text-gray-300`,
											yearsList: "w-full",
										}}
									/>
								</DatesProvider>
							</div>
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			)}
		</>
	);
});

DeliveryPeriodFilter.displayName = "DeliveryPeriodFilter";

export default DeliveryPeriodFilter;